import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Eye, EyeOff, Shield, User, QrCode } from 'lucide-react';

type Step = 1 | 2 | 3;

interface SetupResponse {
  success: boolean;
  totpSecret: string;
  otpauthUrl: string;
  qrCode: string;
}

export default function SetupPage() {
  const navigate = useNavigate();

  const [step, setStep]                 = useState<Step>(1);
  const [username, setUsername]         = useState('');
  const [displayName, setDisplayName]   = useState('');
  const [password, setPassword]         = useState('');
  const [confirmPwd, setConfirmPwd]     = useState('');
  const [showPwd, setShowPwd]           = useState(false);
  const [showConfirm, setShowConfirm]   = useState(false);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [qrCode, setQrCode]             = useState('');
  const [totpSecret, setTotpSecret]     = useState('');

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !displayName.trim() || !password) {
      setError('All fields are required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPwd) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post<SetupResponse>('/api/setup', {
        username:    username.trim().toLowerCase(),
        password,
        displayName: displayName.trim(),
      });
      setQrCode(res.data.qrCode);
      setTotpSecret(res.data.totpSecret);
      setStep(2);
    } catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data?.error || 'Setup failed. Please try again.');
      } else {
        setError('Setup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    setStep(3);
  };

  const handleGoToLogin = () => {
    navigate('/login');
  };

  /* ── Stepper ─────────────────────────────────────────────────────────── */
  const steps = [
    { num: 1, label: 'Create Account' },
    { num: 2, label: 'Scan QR Code'   },
    { num: 3, label: 'Done'           },
  ];

  return (
    <div className="min-h-screen bg-bg-secondary flex items-center justify-center p-4">
      <div className="w-full max-w-lg">

        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-9 h-9 rounded-lg bg-navy flex items-center justify-center">
              <Shield size={20} className="text-white" />
            </div>
            <span className="text-xl font-bold text-navy">SMART™ Platform</span>
          </div>
          <p className="text-text-secondary text-sm">Initial Setup — Create your admin account</p>
        </div>

        {/* Stepper */}
        <div className="flex items-center justify-center gap-0 mb-8">
          {steps.map((s, idx) => (
            <React.Fragment key={s.num}>
              <div className="flex flex-col items-center gap-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors
                  ${step > s.num
                    ? 'bg-success text-white'
                    : step === s.num
                    ? 'bg-accent text-white'
                    : 'bg-border text-text-tertiary'}`}>
                  {step > s.num ? <CheckCircle size={16} /> : s.num}
                </div>
                <span className={`text-xs whitespace-nowrap ${step === s.num ? 'text-accent font-medium' : 'text-text-tertiary'}`}>
                  {s.label}
                </span>
              </div>
              {idx < steps.length - 1 && (
                <div className={`h-0.5 w-16 mx-1 mb-5 transition-colors ${step > s.num ? 'bg-success' : 'bg-border'}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Card */}
        <div className="bg-white rounded-card border border-border shadow-card p-8">

          {/* ── Step 1: Create Account ── */}
          {step === 1 && (
            <form onSubmit={handleCreateAccount} className="space-y-5">
              <div>
                <h2 className="text-lg font-bold text-text-primary flex items-center gap-2">
                  <User size={18} className="text-accent" />
                  Create Admin Account
                </h2>
                <p className="text-xs text-text-tertiary mt-1">
                  This will be the primary administrator of the SMART™ Platform.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-danger">
                  {error}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    placeholder="e.g. Ganesh Sharma"
                    className="w-full px-3 py-2.5 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                    autoComplete="name"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Username
                  </label>
                  <input
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="e.g. admin"
                    className="w-full px-3 py-2.5 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                    autoComplete="username"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPwd ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Min 8 characters"
                      className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPwd(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                    >
                      {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-text-secondary mb-1.5">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirm ? 'text' : 'password'}
                      value={confirmPwd}
                      onChange={e => setConfirmPwd(e.target.value)}
                      placeholder="Re-enter password"
                      className="w-full px-3 py-2.5 pr-10 rounded-lg border border-border text-sm text-text-primary bg-bg-primary focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent transition-colors"
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm(p => !p)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary hover:text-text-secondary"
                    >
                      {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-accent hover:bg-accent/90 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                {loading ? 'Creating account…' : 'Create Admin Account →'}
              </button>
            </form>
          )}

          {/* ── Step 2: QR Code ── */}
          {step === 2 && (
            <div className="space-y-5 text-center">
              <div>
                <h2 className="text-lg font-bold text-text-primary flex items-center justify-center gap-2">
                  <QrCode size={18} className="text-accent" />
                  Set Up Two-Factor Authentication
                </h2>
                <p className="text-xs text-text-tertiary mt-1">
                  Scan this QR code with Google Authenticator, Authy, or any TOTP app.
                </p>
              </div>

              {qrCode && (
                <div className="flex justify-center">
                  <img
                    src={qrCode}
                    alt="TOTP QR Code"
                    className="w-48 h-48 border border-border rounded-lg p-2"
                  />
                </div>
              )}

              <div className="bg-bg-secondary rounded-lg px-4 py-3 text-left">
                <p className="text-xs text-text-secondary font-semibold mb-1">Manual entry key:</p>
                <p className="font-mono text-sm text-text-primary tracking-wider break-all select-all">
                  {totpSecret}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-left">
                <p className="text-xs text-warning font-medium">⚠ Save this QR code</p>
                <p className="text-xs text-text-secondary mt-0.5">
                  You'll need the authenticator app every time you log in. This screen won't appear again.
                </p>
              </div>

              <button
                onClick={handleDone}
                className="w-full py-2.5 bg-success hover:bg-success/90 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                I've scanned the QR code →
              </button>
            </div>
          )}

          {/* ── Step 3: Done ── */}
          {step === 3 && (
            <div className="space-y-5 text-center">
              <div className="flex justify-center">
                <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center">
                  <CheckCircle size={36} className="text-success" />
                </div>
              </div>
              <div>
                <h2 className="text-xl font-bold text-text-primary">Setup Complete!</h2>
                <p className="text-sm text-text-secondary mt-2">
                  Your admin account has been created. You can now log in to the SMART™ Platform.
                </p>
              </div>
              <div className="bg-bg-secondary rounded-lg px-4 py-3 text-left text-xs text-text-secondary space-y-1">
                <p>✓ Admin account created</p>
                <p>✓ Two-factor authentication configured</p>
                <p>✓ Encrypted user store initialised</p>
              </div>
              <button
                onClick={handleGoToLogin}
                className="w-full py-2.5 bg-navy hover:bg-navy/90 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                Go to Login
              </button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-text-tertiary mt-6">
          SMART™ Platform · Secured by Avion
        </p>
      </div>
    </div>
  );
}
